import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import {
  PanelHeader,
  Button,
  Divider,
  Tabs,
  Drawer,
} from "@pushpress/shared-components";
import ClassDetails from "./ClassDetails";
import ClassSettings from "./ClassSettings";
import {
  calendarItemRecurrence,
  reservationsOpenOptions,
  reservationsCloseOptions,
  lateCancelOptions,
} from "../../Calendar.utils";
import { getWeekDay } from "../../../../utils/dateUtils";
import {
  ClassForm,
  parseCalendarFormToCreateClassTemplateMutationInputValue,
  parseClassFormDefaultValues,
} from "./../CalendarForm.utils";
import { useToggle } from "../../../../hooks/state";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../utils/misc";
import {
  GetCalendarItemForEditQuery,
  useCreateCalendarTemplateMutation,
  useGetCalendarItemForEditQuery,
} from "./../CalendarForm-generated.hooks";
import { useConfirm } from "../../../../components/providers/ConfirmProvider";
import { AppContext } from "../../../../context/app";

type CreateClassProps = {
  visible: boolean;
  duplicateCalendarItemUuid?: string;
  initialDate?: string;
  initialHour?: string;
  endHour?: string;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

const CreateClassDrawer = (props: CreateClassProps) => {
  const { client } = useContext(AppContext);
  const { data } = useGetCalendarItemForEditQuery({
    variables: {
      getCalendarItemInput: {
        uuid: props.duplicateCalendarItemUuid || "",
        clientUuid: client.uuid,
      },
    },
    skip: !props.duplicateCalendarItemUuid,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  if (props.duplicateCalendarItemUuid) {
    return (
      <Drawer visible={props.visible} anchor="right" width="420px">
        {props.visible && !!data?.getCalendarItem && (
          <CreateClass
            {...props}
            duplicateCalendarItemDefaultValues={data.getCalendarItem}
          />
        )}
      </Drawer>
    );
  }

  return (
    <Drawer visible={props.visible} anchor="right" width="420px">
      {props.visible && <CreateClass {...props} />}
    </Drawer>
  );
};

const CreateClass = (
  props: CreateClassProps & {
    duplicateCalendarItemDefaultValues?: GetCalendarItemForEditQuery["getCalendarItem"];
  },
) => {
  const {
    onClose,
    initialDate = format(new Date(), "yyyy-MM-dd"),
    initialHour,
    endHour,
    onSuccess,
  } = props;
  const { showDiscardConfirm } = useConfirm();
  const [loadingCreateClass, isLoadingCreateClassActions] = useToggle();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [createCalendarTemplateMutation] = useCreateCalendarTemplateMutation();

  const defaultValues: Partial<ClassForm> =
    props.duplicateCalendarItemDefaultValues
      ? parseClassFormDefaultValues(
          props.duplicateCalendarItemDefaultValues,
          !!props.duplicateCalendarItemUuid,
        )
      : {
          date: {
            start: initialDate,
          },
          time: {
            start: initialHour,
            end: endHour,
          },
          recurrence: calendarItemRecurrence[0],
          classEnds: {
            type: "never",
            number: 1,
            date: initialDate,
          },
          reservationsOpen: reservationsOpenOptions[0],
          reservationsClose: reservationsCloseOptions[0],
          lateCancel: lateCancelOptions[0],
          attendanceLimit: {
            hasAttendanceLimit: false,
            number: 25,
          },
          weekDays: initialDate
            ? [getWeekDay(initialDate)]
            : [format(new Date(), "EEEE")],
        };

  const {
    control,
    handleSubmit,
    trigger,
    formState: { isDirty },
  } = useForm<ClassForm>({
    mode: "onTouched",
    defaultValues,
  });

  const save = async (values: ClassForm) => {
    try {
      isLoadingCreateClassActions.on();
      const variables =
        parseCalendarFormToCreateClassTemplateMutationInputValue(values);

      await createCalendarTemplateMutation({
        variables,
      });
      toast.success(`Class successfully created!`, defaultToastOptions);
      onSuccess();
    } catch (err) {
      toast.error(`Error creating class: ${err}`, defaultToastOptions);
    } finally {
      isLoadingCreateClassActions.off();
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      showDiscardConfirm({ onConfirm: onClose });
    } else {
      onClose();
    }
  };

  return (
    <>
      <PanelHeader
        title={`${
          props.duplicateCalendarItemDefaultValues ? "Duplicate" : "Create"
        } class`}
      >
        <PanelHeader.ActionMenu>
          <Button
            text="Cancel"
            buttonType="secondary"
            size="medium"
            onClick={handleCancel}
          />
          <Button
            text="Save"
            buttonType="primary"
            loading={loadingCreateClass}
            size="medium"
            iconLocation="left"
            onClick={handleSubmit(save)}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <Divider />
      <Tabs
        tabHeaders={["Details", "Settings"]}
        selectedIndex={selectedTabIndex}
        changeTab={setSelectedTabIndex}
        onTabClick={setSelectedTabIndex}
        align="left"
        hideWithCss
        onTabFocus={setSelectedTabIndex}
      >
        <ClassDetails control={control} trigger={trigger} />
        <ClassSettings control={control} />
      </Tabs>
    </>
  );
};

export default CreateClassDrawer;
